// The screens list, driven by `POST /v1/workspaces/{ws}/screens/search` —
// every display across all locations in one workspace-wide table. Mirrors
// locations.index.tsx's shape: free text is local (a keystroke per history entry
// is worse than a non-shareable search box), filters live in the URL via
// kore's filter-url grammar, and the sort is local for the same reason as
// the query. The cursor lives in `useCursorStack`.
//
// "Mirrors" is now literal: the URL dict, the query/scope/sort/page-size
// state and the request predicate all come from `usePageSearch`, shared with
// locations.index.tsx, because two copies of that wiring is what let the two
// pages diverge — see that hook's header.
//
// The free-text input is kore's own: `SearchBar` renders it (and debounces
// it, 200ms) once both `query` and `onQueryChange` are wired below —
// omitting either was this page's old mistake, and the reason its
// `FILTERED BY` bar used to render squeezed beside a hand-rolled search
// box instead of as its own full-width row underneath. `query` here
// already reflects kore's debounce; there is no second one to add.
// `screensSearchManifest` declares two query fields (screen name, location
// name), so kore also renders a scope chip — `searchBy` tracks whichever
// the user picked.
//
// The Overview still renders every screen from the status endpoint,
// untouched — its redesign is a separate, acknowledged piece of work.
// Screens are still created against a location — the dialog picks it
// (auto-selected when there's exactly one).

import { Button, DataTable, type DataTableColumn, PageHead, SearchBar, useKoreSearch } from "@pej/kore/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocations } from "@/api/locations";
import { useScreensSearch } from "@/api/search";
import { useTemplates } from "@/api/templates";
import type { ScreenSearchEntry, ScreenSearchQueryField, ScreenSortField } from "@/api/types";
import { pinnedActionsTable, rowActionsColumn } from "@/components/row-actions";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { ScreenDeleteConfirm, type ScreenDeleteTarget } from "@/features/screens/screen-delete-confirm";
import { ScreenFreshnessBadge } from "@/features/screens/screen-freshness";
import {
    SCREEN_DEFAULT_ORDER,
    SCREEN_QUERY_FIELDS,
    SCREEN_SORT_FIELDS,
    SCREENS_PAGE_SIZE_OPTIONS,
    type ScreenSearchBarFilters,
    screenFiltersForWire,
    screensSearchManifest,
} from "@/features/screens/screens-filters";
import { usePageSearch } from "@/features/search/page-search";
import { sortableHeader } from "@/features/search/sortable-header";
import { useCursorStack } from "@/features/search/use-cursor-stack";
import { useNow } from "@/lib/use-now";

export const Route = createFileRoute("/w/$workspaceId/screens/")({
    component: ScreensPage,
});

function ScreensPage() {
    const { workspaceId } = useWorkspaceContext();
    const navigate = useNavigate();
    const [deleteTarget, setDeleteTarget] = useState<ScreenDeleteTarget | null>(null);
    const page = usePageSearch<ScreenSortField, ScreenSearchQueryField>({
        sortFields: SCREEN_SORT_FIELDS,
        defaultOrder: SCREEN_DEFAULT_ORDER,
        queryFields: SCREEN_QUERY_FIELDS,
        pageSizeOptions: SCREENS_PAGE_SIZE_OPTIONS,
    });

    // The location/template pickers need real options, not just a facet
    // declaration — `useLocations` is already fetched for the create dialog, but
    // that's a separate hook instance (own component, own cache read by the
    // same query key). `useTemplates` is new here.
    const locationsQuery = useLocations(workspaceId);
    const templatesQuery = useTemplates(workspaceId);
    // Memoized on the two `data` identities: `useLocations` polls on an interval,
    // and a fresh `values` array on every poll would churn `useKoreSearch`'s
    // internal memos, which key off `manifest` by reference.
    const manifest = useMemo(
        () => screensSearchManifest(locationsQuery.data, templatesQuery.data),
        [locationsQuery.data, templatesQuery.data],
    );

    const search = useKoreSearch<ScreenSearchBarFilters>({
        manifest,
        router: { search: page.rawSearch, onChange: page.onSearchPatch },
        orderBy: page.orderBy,
        onOrderByChange: page.onOrderByChange,
        // Wiring both is what makes `SearchBar` render its own query input
        // as row one of its two-row layout — see the file header.
        query: page.query,
        onQueryChange: page.setQuery,
        searchBy: page.searchBy,
        onSearchByChange: page.onSearchByChange,
        // Explicit, not left to the manifest: once `query.fields` is
        // non-empty, kore's own placeholder derivation (search-bar/index.tsx
        // `derivedPlaceholder`) always builds "Search by name, location…" from
        // the field labels and ignores `manifest.query.placeholder` — only
        // this prop wins over that.
        searchPlaceholder: "Search screens…",
    });

    // Every field kore builds here is still the raw `Enum` chip
    // `screenFilterDefs` produces, not the wire shape `screens_search.rs`
    // expects — and the translation also drops operators a hand-edited URL can
    // produce despite the defs offering only `Eq`. See
    // `screenFiltersForWire`'s doc.
    const predicate = page.predicate(screenFiltersForWire(search.rq?.filters));

    const cursor = useCursorStack({ request: predicate });
    const screensQuery = useScreensSearch(workspaceId, { ...predicate, pageToken: cursor.pageToken });
    const pager = cursor.pager(screensQuery.data?.nextPageToken ?? null);

    const isSortable = (key: string) => search.sortableColumnKeys.has(key);
    // One clock for the whole table, like locations-table.tsx: badges in
    // different rows must not disagree about what "now" is.
    const now = useNow();

    const columns: DataTableColumn<ScreenSearchEntry>[] = [
        {
            key: "Name",
            header: sortableHeader("Name", isSortable("Name")),
            sortable: isSortable("Name"),
            cell: (row) => <span className="font-medium">{row.name}</span>,
        },
        {
            key: "LocationName",
            header: sortableHeader("Location", isSortable("LocationName")),
            sortable: isSortable("LocationName"),
            cell: (row) => <span className="text-muted-foreground">{row.locationName}</span>,
        },
        {
            // Lowercase key, like `latest` and `status` below: the convention
            // in this table is that a capitalised key is a `ScreenSortField`
            // the server sorts on, and these three are not.
            key: "template",
            header: "Template",
            cell: (row) => <span className="text-muted-foreground">{row.templateName ?? "—"}</span>,
        },
        {
            // The Overview's ladder, not a second Active/Inactive badge: the
            // Template and Active facets both used to filter on facts no
            // column showed, and of the two readings of "is this screen
            // working" — switched on, and actually being fed — only one badge
            // can be on screen without the reader having to adjudicate.
            // `screen-freshness.tsx` holds the rung order.
            key: "status",
            header: "Status",
            cell: (row) => (
                <ScreenFreshnessBadge
                    active={row.active}
                    serviceLastSeenAt={row.serviceLastSeenAt}
                    latestComputedAt={row.latest?.computedAt ?? null}
                    now={now}
                />
            ),
        },
        {
            // Lowercase, deliberately unsortable: the lateral join `latest`
            // comes from is neither sortable nor filterable server-side (a
            // keyset page boundary over a continuously-changing joined value
            // has no established need behind it — see screens_search.rs).
            key: "latest",
            header: "Shows now",
            align: "right",
            cell: (row) => (
                <span className="tabular-nums">{row.latest ? `${row.latest.displayedMinutes} min` : "—"}</span>
            ),
        },
        {
            key: "Offset",
            header: sortableHeader("Offset", isSortable("Offset")),
            // Right, like `latest` beside it: two tabular-nums columns that
            // disagree about their edge read as a layout bug.
            align: "right",
            sortable: isSortable("Offset"),
            cell: (row) => <span className="tabular-nums">{row.offsetMinutes} min</span>,
        },
        rowActionsColumn(
            (row) => `Actions for ${row.name}`,
            (row) => [
                {
                    id: "delete",
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(row),
                },
            ],
        ),
    ];

    return (
        <div className="flex h-full flex-col gap-6">
            {/* h-full, so the table below fills the viewport instead of
                growing the page: `main` has a definite height (flex-1 inside
                the h-screen, overflow-hidden shell), so an h-full child fits
                exactly and no outer scrollbar appears. The table takes the
                remaining height and scrolls its own rows — see the
                `min-h-0 flex-1` on it, and kore's DataTable root, which is
                flex-col precisely so a height-constrained container pins its
                pager to the bottom. Same shape as the vouchers admin's
                crud-page. */}
            <PageHead
                size="h1"
                title="Screens"
                description="Every display across all locations — click through for offset, history, and setup."
                action={
                    <Button onClick={() => navigate({ to: "/w/$workspaceId/screens/new", params: { workspaceId } })}>
                        <Plus aria-hidden="true" />
                        Create screen
                    </Button>
                }
            />
            {/* Sibling of the table, not nested inside it: kore's own table
                card has its own borders and padding, and stacking the search
                row inside would nest one bordered bar inside another and
                inherit padding meant for the card, not the bar. */}
            {/* compact, not kore's default: `size="comfortable"` gives a 36px
                field and 36px filter pills, where every other table on the
                platform runs the 28px pair. Taking the library default by
                omission is what made these toolbars roomier than the rest. */}
            <SearchBar {...search.barProps} size="compact" />
            <DataTable
                className={`min-h-0 flex-1 ${pinnedActionsTable}`}
                density="compact"
                caption="Screens"
                columns={columns}
                rows={screensQuery.data?.items ?? []}
                rowKey={(row) => row.id}
                // Error wins over loading: a retry leaves the previous failure
                // on screen while it flies, and "loading" would blank the
                // message the user is deciding about.
                state={screensQuery.isError ? "error" : screensQuery.isLoading ? "loading" : undefined}
                error={{
                    title: "Couldn't load screens",
                    detail: "Nothing was filtered out — the list itself didn't load.",
                    onRetry: () => void screensQuery.refetch(),
                }}
                sort={search.tableSortProps.sort}
                onSortChange={search.tableSortProps.onSortChange}
                empty={
                    page.searchText !== "" || search.filters.length > 0
                        ? {
                              headline: "No screens match",
                              body: "Try a different search, or clear a filter.",
                          }
                        : {
                              headline: "No screens yet",
                              body: "Every screen belongs to a location. Add a location first if there isn't one yet.",
                          }
                }
                pagination={{
                    ...pager,
                    pageSize: page.pageSize,
                    totalCount: screensQuery.data?.totalCount ?? 0,
                    pageSizeOptions: SCREENS_PAGE_SIZE_OPTIONS,
                    onPageSizeChange: page.setPageSize,
                    entityLabel: "screen",
                }}
                onRowClick={(row) =>
                    void navigate({
                        to: "/w/$workspaceId/screens/$screenId",
                        params: { workspaceId, screenId: row.id },
                    })
                }
            />
            <ScreenDeleteConfirm
                workspaceId={workspaceId}
                screen={deleteTarget}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}
